import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Building2,
  Sparkles,
  Loader2,
  Save,
  BookOpen,
  Target as TargetIcon
} from 'lucide-react';
import onboardingService, { BusinessProfile } from '../../services/onboardingService';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';

const BRAND_VOICE_VALUES: Array<NonNullable<BusinessProfile['brand_voice']>> = [
  'professional', 'casual', 'friendly', 'formal', 'bold', 'inspirational', 'educational'
];

const MARKETING_GOAL_VALUES = [
  'awareness', 'leads', 'sales', 'engagement', 'traffic', 'community', 'education'
];

const BusinessProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<BusinessProfile | null>(null);

  const [form, setForm] = useState({
    business_name: '',
    business_description: '',
    industry: '',
    brand_voice: 'professional' as BusinessProfile['brand_voice'],
    audience_description: '',
    ai_context: '',
    marketing_goals: [] as string[]
  });

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await onboardingService.getProfile();
        if (!active) return;
        if (data) {
          setProfile(data);
          setForm({
            business_name: data.business_name || '',
            business_description: data.business_description || '',
            industry: data.industry || '',
            brand_voice: data.brand_voice || 'professional',
            audience_description: data.target_audience?.description || '',
            ai_context: data.ai_context || '',
            marketing_goals: Array.isArray(data.marketing_goals) ? data.marketing_goals : []
          });
        }
      } catch {
        toast.error(t('businessProfile.loadError', 'Could not load business profile.'));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [t]);

  const toggleGoal = (goal: string) => {
    setForm(prev => ({
      ...prev,
      marketing_goals: prev.marketing_goals.includes(goal)
        ? prev.marketing_goals.filter(g => g !== goal)
        : [...prev.marketing_goals, goal]
    }));
  };

  const handleSave = async () => {
    if (!form.business_name.trim()) {
      toast.error(t('businessProfile.nameRequired', 'Business name is required.'));
      return;
    }
    setSaving(true);
    try {
      const updates: Partial<BusinessProfile> = {
        business_name: form.business_name.trim(),
        business_description: form.business_description.trim() || undefined,
        industry: form.industry || undefined,
        brand_voice: form.brand_voice,
        ai_context: form.ai_context.trim() || undefined,
        marketing_goals: form.marketing_goals,
        target_audience: {
          ...(profile?.target_audience || {}),
          description: form.audience_description.trim() || undefined
        }
      };
      const saved = await onboardingService.updateProfile(updates);
      setProfile(saved);
      toast.success(t('businessProfile.saveSuccess', 'Business profile saved. AI will use this context on the next posts.'));
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('businessProfile.saveError', 'Error saving profile.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto sm:px-4 py-2 sm:py-8">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('common.back', 'Back')}
      </button>

      <div className="flex items-start gap-3 mb-6">
        <div className="hidden sm:flex w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 items-center justify-center text-white shrink-0">
          <Building2 className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {t('businessProfile.title', 'Business Profile')}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('businessProfile.subtitle', 'The AI uses this information as a base to generate posts, captions and campaigns in your tone and about what matters to your business.')}
          </p>
        </div>
      </div>

      <Card className="!p-4 sm:!p-6">
        <CardContent className="p-0 sm:p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('businessProfile.businessName', 'Business name')} <span className="text-red-500">*</span>
            </label>
            <Input
              value={form.business_name}
              onChange={(e) => setForm(prev => ({ ...prev, business_name: e.target.value }))}
              placeholder={t('businessProfile.businessNamePlaceholder', 'E.g.: Acme Inc.') as string}
              maxLength={255}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('businessProfile.description', 'Short business description')}
            </label>
            <textarea
              value={form.business_description}
              onChange={(e) => setForm(prev => ({ ...prev, business_description: e.target.value }))}
              rows={3}
              placeholder={t('businessProfile.descriptionPlaceholder', 'What you do, who for, your differentiator…') as string}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-1 focus:ring-purple-500 bg-white text-gray-900 dark:bg-gray-800 dark:text-white dark:border-gray-600"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('businessProfile.industry', 'Industry')}
              </label>
              <Input
                value={form.industry}
                onChange={(e) => setForm(prev => ({ ...prev, industry: e.target.value }))}
                placeholder={t('businessProfile.industryPlaceholder', 'E.g.: marketing, health, retail…') as string}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('businessProfile.brandVoice', 'Brand voice')}
              </label>
              <select
                value={form.brand_voice}
                onChange={(e) => setForm(prev => ({ ...prev, brand_voice: e.target.value as BusinessProfile['brand_voice'] }))}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-1 focus:ring-purple-500 bg-white text-gray-900 dark:bg-gray-800 dark:text-white dark:border-gray-600"
              >
                {BRAND_VOICE_VALUES.map(v => (
                  <option key={v} value={v}>
                    {t(`businessProfile.voices.${v}.label`, v)} — {t(`businessProfile.voices.${v}.desc`, '')}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <span className="inline-flex items-center gap-2">
                <TargetIcon className="w-4 h-4 text-gray-400" />
                {t('businessProfile.audience', 'Target audience (free description)')}
              </span>
            </label>
            <textarea
              value={form.audience_description}
              onChange={(e) => setForm(prev => ({ ...prev, audience_description: e.target.value }))}
              rows={2}
              placeholder={t('businessProfile.audiencePlaceholder', 'E.g.: digital entrepreneurs aged 25–45 who want to scale marketing without a huge team.') as string}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-1 focus:ring-purple-500 bg-white text-gray-900 dark:bg-gray-800 dark:text-white dark:border-gray-600"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('businessProfile.goals', 'Marketing goals')}
            </label>
            <div className="flex flex-wrap gap-2">
              {MARKETING_GOAL_VALUES.map(g => {
                const active = form.marketing_goals.includes(g);
                return (
                  <button
                    key={g}
                    type="button"
                    onClick={() => toggleGoal(g)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                      active
                        ? 'bg-purple-600 text-white border-purple-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-purple-300 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600'
                    }`}
                  >
                    {t(`businessProfile.goalsOptions.${g}`, g)}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="border-t dark:border-gray-700 pt-6">
            <div className="flex items-start gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-purple-500 mt-0.5" />
              <div>
                <label className="block text-sm font-medium text-gray-800 dark:text-gray-200">
                  {t('businessProfile.aiContext', 'Free-text context for the AI')}
                </label>
                <p className="text-xs text-gray-500 mt-0.5">
                  {t('businessProfile.aiContextHelp', 'In free text, tell the AI what it needs to know about your business: differentiators, terms you avoid, industry jargon, ongoing campaigns, etc. The more specific, the better.')}
                </p>
              </div>
            </div>
            <textarea
              value={form.ai_context}
              onChange={(e) => setForm(prev => ({ ...prev, ai_context: e.target.value }))}
              rows={6}
              maxLength={4000}
              placeholder={t('businessProfile.aiContextPlaceholder', 'E.g.: We are an agency that serves medical clinics. We avoid talking about price in posts, we prefer welcoming language, we always mention "humanized care". We do not overuse exclamations. The ideal avatar is the clinic manager/owner (not the doctor).') as string}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-1 focus:ring-purple-500 bg-white text-gray-900 dark:bg-gray-800 dark:text-white dark:border-gray-600 font-mono leading-relaxed"
            />
            <div className="text-xs text-gray-400 text-right mt-1">{form.ai_context.length}/4000</div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 border-t dark:border-gray-700">
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <BookOpen className="w-4 h-4" />
              <span>{t('businessProfile.footerHint', 'The AI also looks at your recent posts to keep the pattern. You can turn this off per post.')}</span>
            </div>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full sm:w-auto h-11 sm:h-10 shrink-0"
              leftIcon={saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            >
              {saving ? t('common.saving', 'Saving…') : t('businessProfile.save', 'Save profile')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BusinessProfilePage;
